import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canApprovePto } from '@/lib/permissions';
import { countsAgainstPtoBalance } from '@/features/pto/format';
import type { Inserts, PtoLeaveType, PtoStatus, Tables } from '@/types/database';

/**
 * Hooks for PTO tracking (migration 95).
 *
 * Access model (DB-enforced via RLS):
 *   * Every non-lender member can submit + read their OWN requests and award,
 *     and cancel their own pending requests.
 *   * The approver tier (super_admin / admin / manager) can read every
 *     request + award and approve / reject requests.
 *   * The awards tier (super_admin / admin) sets the per-person annual award.
 *
 * The "remaining balance" is derived, never stored — see computePtoBalance().
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type PtoAward = Tables<'pto_awards'>;
export type PtoRequest = Tables<'pto_requests'>;

export type PtoPersonProfile = Pick<
  Tables<'profiles'>,
  'id' | 'full_name' | 'email' | 'avatar_url'
>;

/** A request row with the requester + reviewer profiles flattened in. */
export type PtoRequestWithPeople = PtoRequest & {
  requester: PtoPersonProfile | null;
  reviewer: PtoPersonProfile | null;
};

/** Derived per-person, per-year balance summary. */
export interface PtoBalance {
  year: number;
  awardedDays: number;
  carryoverDays: number;
  /** awarded + carryover. */
  totalDays: number;
  /** Sum of approved request days in the year. */
  usedDays: number;
  /** Sum of pending request days in the year (not yet deducted). */
  pendingDays: number;
  /** total − used. */
  remainingDays: number;
}

// ---------------------------------------------------------------------------
// Query-key helpers
// ---------------------------------------------------------------------------

export const ptoKeys = {
  all: ['pto'] as const,
  myRequests: (profileId: string) =>
    [...ptoKeys.all, 'my-requests', profileId] as const,
  allRequests: (status: PtoStatus | 'all') =>
    [...ptoKeys.all, 'all-requests', status] as const,
  myAward: (profileId: string, year: number) =>
    [...ptoKeys.all, 'my-award', profileId, year] as const,
  awards: (year: number) => [...ptoKeys.all, 'awards', year] as const,
};

const REQUEST_SELECT =
  'id, organization_id, profile_id, start_date, end_date, days, reason, ' +
  'leave_type, bereavement_relationship, status, ' +
  'reviewed_by, reviewed_at, review_note, created_at, updated_at, ' +
  'requester:profiles!pto_requests_profile_id_fkey(id, full_name, email, avatar_url), ' +
  'reviewer:profiles!pto_requests_reviewed_by_fkey(id, full_name, email, avatar_url)';

export const currentPtoYear = () => new Date().getFullYear();

/** The calendar year a request counts against (its start date). */
export function ptoRequestYear(request: Pick<PtoRequest, 'start_date'>): number {
  return new Date(request.start_date).getFullYear();
}

/**
 * Derive a balance summary from an award + the requester's own requests.
 * Only requests whose start_date falls in `year` are counted so a balance
 * always reflects a single leave year. Bereavement leave is excluded — it
 * does not consume the annual PTO award (migration 97).
 */
export function computePtoBalance(
  year: number,
  award: PtoAward | null,
  requests: Pick<PtoRequest, 'start_date' | 'days' | 'status' | 'leave_type'>[],
): PtoBalance {
  const awardedDays = award?.awarded_days ?? 0;
  const carryoverDays = award?.carryover_days ?? 0;
  const inYear = requests.filter(
    (r) =>
      ptoRequestYear(r) === year && countsAgainstPtoBalance(r.leave_type),
  );
  const usedDays = inYear
    .filter((r) => r.status === 'approved')
    .reduce((sum, r) => sum + Number(r.days), 0);
  const pendingDays = inYear
    .filter((r) => r.status === 'pending')
    .reduce((sum, r) => sum + Number(r.days), 0);
  const totalDays = awardedDays + carryoverDays;
  return {
    year,
    awardedDays,
    carryoverDays,
    totalDays,
    usedDays,
    pendingDays,
    remainingDays: totalDays - usedDays,
  };
}

// ---------------------------------------------------------------------------
// My PTO (any member)
// ---------------------------------------------------------------------------

/** Every PTO request belonging to the signed-in user, newest first. */
export function useMyPtoRequests() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: ptoKeys.myRequests(profile?.id ?? 'noop'),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pto_requests')
        .select(REQUEST_SELECT)
        .eq('profile_id', profile!.id)
        .order('start_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PtoRequestWithPeople[];
    },
  });
}

/** The signed-in user's award row for a given year (or null if unset). */
export function useMyPtoAward(year: number) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: ptoKeys.myAward(profile?.id ?? 'noop', year),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pto_awards')
        .select('*')
        .eq('profile_id', profile!.id)
        .eq('year', year)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as PtoAward | null;
    },
  });
}

export interface PtoRequestInput {
  start_date: string;
  end_date: string;
  days: number;
  reason: string | null;
  leave_type: PtoLeaveType;
  bereavement_relationship: string | null;
}

export function useCreatePtoRequest() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: PtoRequestInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const payload: Inserts<'pto_requests'> = {
        organization_id: profile.organization_id,
        profile_id: profile.id,
        start_date: input.start_date,
        end_date: input.end_date,
        days: input.days,
        reason: input.reason,
        leave_type: input.leave_type,
        bereavement_relationship: input.bereavement_relationship,
        status: 'pending',
      };
      const { data, error } = await supabase
        .from('pto_requests')
        .insert(payload)
        .select('id')
        .single();
      if (error) throw error;
      return data as { id: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ptoKeys.all });
    },
  });
}

/** Requester cancels their own pending request. */
export function useCancelPtoRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('pto_requests')
        .update({ status: 'cancelled' })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ptoKeys.all });
    },
  });
}

/** Delete a request outright (own request, or any if you're an approver). */
export function useDeletePtoRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('pto_requests').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ptoKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Manage PTO (approver / awards tiers)
// ---------------------------------------------------------------------------

/** Every request in the org, optionally filtered by status. Approvers only. */
export function useAllPtoRequests(status: PtoStatus | 'all' = 'all') {
  const { profile, role } = useAuth();
  return useQuery({
    queryKey: ptoKeys.allRequests(status),
    enabled: !!profile && canApprovePto(role),
    queryFn: async () => {
      let q = supabase
        .from('pto_requests')
        .select(REQUEST_SELECT)
        .order('start_date', { ascending: false });
      if (status !== 'all') q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as PtoRequestWithPeople[];
    },
  });
}

/** Approve or reject a request. Stamps the reviewer + timestamp. */
export function useReviewPtoRequest() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      status: Extract<PtoStatus, 'approved' | 'rejected'>;
      review_note?: string | null;
    }) => {
      if (!profile?.id) throw new Error('Not signed in.');
      const { error } = await supabase
        .from('pto_requests')
        .update({
          status: input.status,
          reviewed_by: profile.id,
          reviewed_at: new Date().toISOString(),
          review_note: input.review_note ?? null,
        })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ptoKeys.all });
    },
  });
}

/** All award rows for a year with the person profile flattened in. */
export type PtoAwardWithPerson = PtoAward & { person: PtoPersonProfile | null };

export function useAllPtoAwards(year: number) {
  const { profile, role } = useAuth();
  return useQuery({
    queryKey: ptoKeys.awards(year),
    enabled: !!profile && canApprovePto(role),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pto_awards')
        .select(
          '*, person:profiles!pto_awards_profile_id_fkey(id, full_name, email, avatar_url)',
        )
        .eq('year', year);
      if (error) throw error;
      return (data ?? []) as unknown as PtoAwardWithPerson[];
    },
  });
}

/** A trimmed request row used for the org-wide balance + dates drilldown. */
export type PtoYearRequest = Pick<
  PtoRequest,
  | 'id'
  | 'profile_id'
  | 'start_date'
  | 'end_date'
  | 'days'
  | 'status'
  | 'reason'
  | 'leave_type'
  | 'bereavement_relationship'
>;

/** Every request for the org whose start_date falls in the year — used to
 *  compute everyone's used / pending balance AND to list the exact dates each
 *  person took off on the manage page. */
export function usePtoRequestsForYear(year: number) {
  const { profile, role } = useAuth();
  return useQuery({
    queryKey: [...ptoKeys.all, 'year-requests', year] as const,
    enabled: !!profile && canApprovePto(role),
    queryFn: async () => {
      const start = `${year}-01-01`;
      const end = `${year}-12-31`;
      const { data, error } = await supabase
        .from('pto_requests')
        .select(
          'id, profile_id, start_date, end_date, days, status, reason, leave_type, bereavement_relationship',
        )
        .gte('start_date', start)
        .lte('start_date', end)
        .order('start_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as PtoYearRequest[];
    },
  });
}

/**
 * Approver logs PTO on behalf of an employee (migration 96). Defaults to an
 * 'approved' record — the common case is backfilling time off already taken —
 * but can also create a pending request. When approved/rejected we stamp the
 * acting approver as the reviewer.
 */
export function useLogPtoForEmployee() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      profile_id: string;
      start_date: string;
      end_date: string;
      days: number;
      reason: string | null;
      leave_type: PtoLeaveType;
      bereavement_relationship: string | null;
      status: PtoStatus;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const reviewed = input.status === 'approved' || input.status === 'rejected';
      const payload: Inserts<'pto_requests'> = {
        organization_id: profile.organization_id,
        profile_id: input.profile_id,
        start_date: input.start_date,
        end_date: input.end_date,
        days: input.days,
        reason: input.reason,
        leave_type: input.leave_type,
        bereavement_relationship: input.bereavement_relationship,
        status: input.status,
        reviewed_by: reviewed ? profile.id : null,
        reviewed_at: reviewed ? new Date().toISOString() : null,
      };
      const { data, error } = await supabase
        .from('pto_requests')
        .insert(payload)
        .select('id')
        .single();
      if (error) throw error;
      return data as { id: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ptoKeys.all });
    },
  });
}

/** Create or update a person's annual award (admin / super_admin). */
export function useUpsertPtoAward() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      profile_id: string;
      year: number;
      awarded_days: number;
      carryover_days: number;
      note: string | null;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const payload: Inserts<'pto_awards'> = {
        organization_id: profile.organization_id,
        profile_id: input.profile_id,
        year: input.year,
        awarded_days: input.awarded_days,
        carryover_days: input.carryover_days,
        note: input.note,
        created_by: profile.id,
      };
      const { data, error } = await supabase
        .from('pto_awards')
        .upsert(payload, { onConflict: 'organization_id,profile_id,year' })
        .select('id')
        .single();
      if (error) throw error;
      return data as { id: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ptoKeys.all });
    },
  });
}
